import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import { formatTakaNo, seriesLabel } from '../utils/taka';
import { useCanWrite } from '../components/Can';
import { usePermissions } from '../hooks/usePermissions';
import TakaGroupsPanel from '../components/TakaGroupsPanel';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyScope } from '../hooks/useCompanyScope';
import CompanyCell from '../components/CompanyCell';
import CompanySelect from '../components/CompanySelect';

interface TakaEntry {
  id?: number;
  takaNo: string;
  date?: string;
  beamNo?: string;
  meter: string;
  saree: string;
  mcNo: string;
  qualityDesiNo: string;
  colour: string;
  weight: string;
  avgPick: string;
  createdAt?: string;
  status?: 'pending' | 'saved' | 'in_stock' | 'dispatched';
  // Original date loaded from DB for an existing row; used to preserve date on update
  originalDate?: string;
}

interface Quality {
  id: number;
  name: string;
  isActive: boolean;
  colour?: string | null;
  pick?: number | null;
}

const INITIAL_ROWS = 1;

// Below `md` the dense tables are swapped for stacked cards.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

const emptyRow = (takaNo = ''): TakaEntry => ({
  takaNo, meter: '', saree: '', mcNo: '', qualityDesiNo: '', colour: '', weight: '', avgPick: ''
});

const generateRows = (count: number, startNo: number): TakaEntry[] =>
  Array.from({ length: count }, (_, i) => emptyRow(String(startNo + i)));

const isRowEmpty = (r: TakaEntry) =>
  !r.meter && !r.saree && !r.mcNo && !r.qualityDesiNo && !r.colour && !r.weight && !r.avgPick;

// Meter must be <= 999, decimals allowed (max 2 places). Empty allowed for clearing.
const isMeterValid = (v: string): boolean => {
  if (v === '' || v === '.') return true;
  if (!/^\d{0,3}(\.\d{0,2})?$/.test(v)) return false;
  const n = parseFloat(v);
  return isNaN(n) || n <= 999;
};

// Searchable Quality Dropdown
const QualityDropdown: React.FC<{
  value: string;
  onChange: (v: string) => void;
  qualities: Quality[];
  onCreateQuality: (name: string) => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  onEnterCommit?: () => void;
  // Fires only on an explicit pick (click/Enter/Tab) with the matched quality — not on typing.
  onSelectQuality?: (q: Quality) => void;
}> = ({ value, onChange, qualities, onCreateQuality, inputRef: externalRef, onEnterCommit, onSelectQuality }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ top: 0, left: 0, width: 200 });
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const [openUp, setOpenUp] = useState(false);
  useEffect(() => {
    if (!isOpen || !inputRef.current) return;
    const reposition = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const shouldOpenUp = spaceBelow < 200;
      setOpenUp(shouldOpenUp);
      // Cap the width to the viewport and keep the panel on-screen on narrow displays.
      const width = Math.min(Math.max(r.width, 200), window.innerWidth - 16);
      setDropdownPos({
        top: shouldOpenUp ? undefined : r.bottom + 2,
        bottom: shouldOpenUp ? (window.innerHeight - r.top + 2) : undefined,
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
        width
      });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  const term = isOpen ? search : value;
  const filtered = qualities.filter(q => q.isActive && q.name.toLowerCase().includes((term || '').toLowerCase()));
  // Canonical matching: trim + collapse internal spaces + case-insensitive. This is the
  // lock that stops "SS Chanderi" / "SS CHANDERI" / "ss chanderi " becoming separate rows.
  const norm = (s: string) => (s || '').trim().replace(/\s+/g, ' ');
  const canonKey = (s: string) => norm(s).toLowerCase();
  const findCanonical = (t: string) => qualities.find(q => q.isActive && canonKey(q.name) === canonKey(t));
  const showCreate = !!norm(term) && !findCanonical(term);

  // Reset highlight when filtered list changes
  useEffect(() => { setHighlightIdx(0); }, [filtered.length]);

  const selectAndClose = (name: string) => {
    onChange(name);
    // Auto-populate colour/pick when the picked name matches a known quality.
    const picked = qualities.find(q => q.name === name);
    if (picked) onSelectQuality?.(picked);
    setSearch('');
    setIsOpen(false);
  };

  // Resolve a typed term: snap to the existing canonical name if one matches,
  // otherwise create a new (normalized) quality. Never stores a case/space variant.
  const commit = (rawTerm: string) => {
    const canon = findCanonical(rawTerm);
    if (canon) { selectAndClose(canon.name); return; }
    const clean = norm(rawTerm);
    if (clean) { onCreateQuality(clean); selectAndClose(clean); }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter') {
        // Closed dropdown + Enter on a field that already has a value: advance
        e.preventDefault();
        if (value) {
          onEnterCommit?.();
        } else {
          setIsOpen(true);
          setSearch('');
        }
        return;
      }
      // Only open on typing, not Tab
      if (e.key !== 'Tab' && e.key !== 'Shift') {
        setIsOpen(true);
        setSearch(value);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) {
        selectAndClose(filtered[highlightIdx]?.name || filtered[0].name);
      } else if (norm(term)) {
        // Snaps to an existing canonical name if one matches, else creates it.
        commit(term);
      } else if (value) {
        // Empty filter but a value already set — just advance
        setIsOpen(false);
      }
      onEnterCommit?.();
    } else if (e.key === 'Tab') {
      // Tab = accept current value and move on. Snap to canonical if it matches an existing quality.
      const t = isOpen ? search : value;
      const canon = findCanonical(t);
      if (canon) {
        selectAndClose(canon.name);
      } else if (filtered.length > 0 && !value) {
        selectAndClose(filtered[highlightIdx]?.name || filtered[0].name);
      }
      setIsOpen(false);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <input
        ref={el => { inputRef.current = el; externalRef?.(el); }}
        type="text"
        value={isOpen ? search : value}
        onChange={(e) => { setSearch(e.target.value); onChange(e.target.value); if (!isOpen) setIsOpen(true); setHighlightIdx(0); }}
        onFocus={() => { setSearch(value); }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Clicking away after typing a case/space variant: snap it to the existing
          // canonical name so no duplicate quality string is stored. (An explicit click
          // on a dropdown item still wins, since its onClick runs after this blur.)
          const t = (inputRef.current && inputRef.current.value) || value;
          const canon = findCanonical(t);
          if (canon && canon.name !== value) onChange(canon.name);
        }}
        placeholder="Search quality..."
        className="w-full px-2 py-1.5 text-sm border-0 bg-transparent focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-400 rounded"
      />
      {isOpen && createPortal(
        <div ref={dropdownRef} className="fixed z-[9999] bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
          style={{ ...(openUp ? { bottom: dropdownPos.bottom } : { top: dropdownPos.top }), left: dropdownPos.left, width: dropdownPos.width }}>
          {filtered.length > 0 ? filtered.map((q, idx) => (
            <button key={q.id} type="button" tabIndex={-1}
              onClick={() => selectAndClose(q.name)}
              className={`w-full text-left px-3 py-1.5 text-sm ${idx === highlightIdx ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-blue-50'}`}
            >{q.name}</button>
          )) : <div className="px-3 py-2 text-xs text-gray-400">No matches</div>}
          {showCreate && (
            <button type="button" tabIndex={-1}
              onClick={() => commit(term)}
              className="w-full text-left px-3 py-1.5 text-sm text-green-700 bg-green-50 hover:bg-green-100 border-t border-gray-100 font-medium"
            >+ Create "{norm(term)}"</button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

// Machine Number Combo Box — type to filter or select, Tab/Enter accepts typed value
const McComboBox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  machines: { id: number; number: string }[];
  inputRef?: (el: HTMLInputElement | null) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}> = ({ value, onChange, machines, inputRef: externalRef, onKeyDown }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ top: 0, left: 0, width: 150 });
  const localRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filtered = machines.filter(m =>
    String(m.number).includes(value)
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (localRef.current && !localRef.current.contains(e.target as Node) &&
          (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [isOpen]);

  const [openUp, setOpenUp] = useState(false);
  const openDrop = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen || !localRef.current) return;
    const reposition = () => {
      if (!localRef.current) return;
      const r = localRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const shouldOpenUp = spaceBelow < 170;
      setOpenUp(shouldOpenUp);
      // Cap the width to the viewport and keep the panel on-screen on narrow displays.
      const width = Math.min(Math.max(r.width, 150), window.innerWidth - 16);
      setDropdownPos({
        top: shouldOpenUp ? undefined : r.bottom + 3,
        bottom: shouldOpenUp ? (window.innerHeight - r.top + 3) : undefined,
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
        width
      });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  return (
    <>
      <input
        ref={el => { localRef.current = el; externalRef?.(el); }}
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); if (!isOpen) openDrop(); }}
        onFocus={openDrop}
        onKeyDown={e => {
          if (e.key === 'Tab' || e.key === 'Enter') setIsOpen(false);
          onKeyDown?.(e);
        }}
        placeholder="-"
        className="w-full px-2 py-1 text-sm border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent"
      />
      {isOpen && filtered.length > 0 && createPortal(
        <div ref={dropdownRef}
          className="fixed z-[9999] bg-white border border-[#E1E6EF] rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] max-h-40 overflow-y-auto py-0.5"
          style={{ ...(openUp ? { bottom: dropdownPos.bottom } : { top: dropdownPos.top }), left: dropdownPos.left, width: dropdownPos.width }}>
          {filtered.map(m => (
            <button key={m.id} type="button" tabIndex={-1}
              onClick={() => { onChange(String(m.number)); setIsOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F8F9FC] transition-colors ${value === String(m.number) ? 'bg-[#F8F9FC] text-[#0055a9] font-medium' : 'text-[#0E131B]'}`}>
              #{m.number}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

const TakaInventory: React.FC = () => {
  const [mode, setMode] = useState<'list' | 'add'>('list');
  // Create-only taka operators (canCreate but not canView) get a locked "add entry" experience:
  // the form opens directly and they never see the inventory list.
  const perms = usePermissions();
  const createOnly = perms.canCreate('taka-inventory') && !perms.canView('taka-inventory');
  const createOnlyRef = useRef(createOnly);
  createOnlyRef.current = createOnly;
  const openAddRef = useRef<() => void>(() => {});
  // Leaving "add" mode: normal users return to the list; create-only users have no list,
  // so we reset to a fresh entry form instead of ever showing the list.
  const exitAddMode = useCallback(() => {
    if (createOnlyRef.current) { openAddRef.current(); return; }
    setMode('list');
  }, []);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [filterDate, setFilterDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [filterQuality, setFilterQuality] = useState<string[]>([]);
  const [filterColour, setFilterColour] = useState<string[]>([]);
  const [filterSeries, setFilterSeries] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [formData, setFormData] = useState({ date: new Date().toISOString().split('T')[0], beamNo: '', startTakaNo: '' });
  const [rows, setRows] = useState<TakaEntry[]>(generateRows(INITIAL_ROWS, 1));
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
  const [newSeriesInput, setNewSeriesInput] = useState<string>('');
  const [showNewSeriesInput, setShowNewSeriesInput] = useState<boolean>(false);
  const [actionMenuId, setActionMenuId] = useState<number | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState<{ action: 'cancel' | 'navigate' } | null>(null);
  const [deletingTakaId, setDeletingTakaId] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const isMobile = useIsMobile();

  const { user } = useAuth();
  const { showCompanyColumn } = useCompanyScope();
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);

  // Close action menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setActionMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const queryClient = useQueryClient();

  // Taka grouping is an opt-in feature: only shown when enabled in the user's preferences.
  const { data: prefsResp } = useQuery({ queryKey: ['preferences'], queryFn: async () => apiService.get('/auth/preferences') });
  const takaGroupsEnabled = !!prefsResp?.preferences?.takaGroupsEnabled;
  const setGroupsEnabledMutation = useMutation({
    mutationFn: async (enabled: boolean) => apiService.put('/auth/preferences', { takaGroupsEnabled: enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['preferences'] }),
  });

  const { data: takaData, isLoading } = useQuery<{ takaInventories: TakaEntry[]; pagination: { total: number; page: number; pageSize: number; totalPages: number } }>({
    queryKey: ['takaInventories', searchQuery, filterMonth, filterDate, filterQuality, filterColour, filterSeries, page, pageSize, takaGroupsEnabled],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      // Skip date/month filter when actively searching so results aren't hidden by today's default
      if (!searchQuery) {
        if (filterDate) {
          params.set('startDate', filterDate);
          params.set('endDate', filterDate);
        } else if (filterMonth) {
          const [y, m] = filterMonth.split('-').map(Number);
          params.set('startDate', `${y}-${String(m).padStart(2, '0')}-01`);
          const lastDay = new Date(y, m, 0).getDate();
          params.set('endDate', `${y}-${String(m).padStart(2, '0')}-${lastDay}`);
        }
      }
      if (filterQuality.length > 0) params.set('quality', filterQuality.join(','));
      if (filterColour.length > 0) params.set('colour', filterColour.join(','));
      if (filterSeries.length === 1) {
        params.set('series', filterSeries[0] === 'Default' ? '' : filterSeries[0]);
      }
      // (multi-series filter is applied client-side; backend supports single-series only)
      // When grouping is enabled, grouped takas live under their group section, so this list
      // shows only ungrouped takas. When disabled, it shows all takas (original behaviour).
      if (takaGroupsEnabled) params.set('ungrouped', 'true');
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const url = `/taka-inventory?${params}`;
      return apiService.get(url);
    }
  });

  const rawTakaList = takaData?.takaInventories || [];
  const takaList = filterSeries.length > 1
    ? rawTakaList.filter((t: any) => filterSeries.includes(t.series === null ? 'Default' : t.series))
    : rawTakaList;
  const pagination = takaData?.pagination || { total: 0, page: 1, pageSize, totalPages: 1 };

  const { data: nextTakaNo } = useQuery({
    queryKey: ['nextTakaNo'],
    queryFn: async () => {
      const data = await apiService.get('/taka-inventory/next-taka-no');
      return data.nextTakaNo || 1;
    }
  });

  const { data: seriesList = [] } = useQuery<{ series: string | null; nextTakaNo: number; count: number }[]>({
    queryKey: ['takaSeriesList'],
    queryFn: async () => {
      const data = await apiService.get('/taka-inventory/series');
      return data.series || [];
    }
  });

  const { data: nextForSelectedSeries } = useQuery({
    queryKey: ['nextTakaNo', selectedSeries],
    queryFn: async () => {
      const q = selectedSeries ? `?series=${encodeURIComponent(selectedSeries)}` : '';
      const data = await apiService.get(`/taka-inventory/next-taka-no${q}`);
      return data.nextTakaNo || 1;
    }
  });

  // Get taka IDs not yet in any challan (these are "In Stock")
  // Until this resolves the set is empty, which would read as "everything dispatched".
  // `stockPending` lets the badge render a neutral placeholder instead of a wrong value.
  const { data: inStockTakaIds = new Set<number>(), isPending: stockPending } = useQuery({
    queryKey: ['inStockTakaIds'],
    queryFn: async () => {
      const data = await apiService.get('/challans/available-takas');
      return new Set((data.takas || []).map((t: any) => t.id));
    }
  });

  const { data: machinesList = [] } = useQuery<{ id: number; number: string }[]>({
    queryKey: ['machines-list'],
    queryFn: async () => {
      const data = await apiService.get('/machines');
      return (data.machines || data || []).map((m: any) => ({ id: m.id, number: String(m.number) }));
    }
  });

  const { data: qualities = [] } = useQuery<Quality[]>({
    queryKey: ['qualities'],
    queryFn: async () => {
      const data = await apiService.get('/qualities');
      return data.qualities || [];
    }
  });

  const { data: distinctQualities = [] } = useQuery<string[]>({
    queryKey: ['distinctTakaQualities'],
    queryFn: async () => {
      const data = await apiService.get('/taka-inventory/distinct-qualities');
      return (data.qualities || []).sort();
    }
  });

  const { data: distinctColours = [] } = useQuery<string[]>({
    queryKey: ['distinctTakaColours'],
    queryFn: async () => {
      const data = await apiService.get('/taka-inventory/distinct-colours');
      return (data.colours || []).sort();
    }
  });

  const createQualityMutation = useMutation({
    mutationFn: async (name: string) => apiService.post('/qualities', { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qualities'] })
  });

  const bulkCreateMutation = useMutation({
    mutationFn: async (data: any) => apiService.post('/taka-inventory', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['takaInventories'] });
      queryClient.invalidateQueries({ queryKey: ['nextTakaNo'] });
      queryClient.invalidateQueries({ queryKey: ['inStockTakaIds'] });
      queryClient.invalidateQueries({ queryKey: ['takaSeriesList'] });
      setSelectedTenantId(null);
      exitAddMode();
      toast.success('Taka entries saved successfully!');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to save')
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => apiService.put(`/taka-inventory/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['takaInventories'] });
      setEditingId(null);
      setEditData({});
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to update')
  });

  // Separate mutation for updating existing entries during add flow (no editingId reset)
  const inlineUpdateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => apiService.put(`/taka-inventory/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['takaInventories'] });
      queryClient.invalidateQueries({ queryKey: ['nextTakaNo'] });
      queryClient.invalidateQueries({ queryKey: ['inStockTakaIds'] });
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to update')
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiService.delete(`/taka-inventory/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['takaInventories'] });
      queryClient.invalidateQueries({ queryKey: ['nextTakaNo'] });
      toast.success('Deleted successfully!');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to delete')
  });

  const canWrite = useCanWrite('taka-inventory');
  const openAdd = () => {
    const startNo = nextTakaNo || 1;
    ++populateVersionRef.current;
    dateUserChangedRef.current = false;
    setFormData({ date: new Date().toISOString().split('T')[0], beamNo: '', startTakaNo: String(startNo) });
    setRows(generateRows(INITIAL_ROWS, startNo));
    savedRowsRef.current = new Set();
    existingRowsRef.current = new Map();
    setSelectedTenantId(null); // reopening the form can't silently reuse the last company
    setMode('add');
    // Check if the first taka number already exists (single call, no duplicates)
    setTimeout(() => checkAndPopulateRow(0, startNo), 100);
  };
  // Keep the ref current so exitAddMode (defined before openAdd) can reset the form.
  openAddRef.current = openAdd;

  // Create-only users have no list to start from — open the entry form on mount.
  useEffect(() => {
    if (createOnly) openAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOnly]);

  const openEdit = (entry: any) => {
    setEditingId(entry.id);
    setEditData({
      takaNo: String(entry.takaNo || ''),
      date: entry.date ? entry.date.split('T')[0] : '',
      beamNo: entry.beamNo || '',
      meter: String(entry.meter ?? ''),
      saree: String(entry.saree ?? ''),
      mcNo: entry.mcNo || '',
      qualityDesiNo: entry.qualityDesiNo || '',
      colour: entry.colour || '',
      weight: String(entry.weight ?? ''),
      avgPick: String(entry.avgPick ?? ''),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  // Debounce timer for DB lookup when spinner changes
  const takaNoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleStartTakaNoChange = (value: string) => {
    // Only allow digits
    const cleaned = value.replace(/\D/g, '');
    setFormData(fd => ({ ...fd, startTakaNo: cleaned }));

    const startNo = parseInt(cleaned);
    if (!startNo || startNo < 1) return;

    // Bump version to discard any in-flight API responses
    ++populateVersionRef.current;

    // Find the last row (the active/current one) and set its taka number directly
    setRows(prev => {
      const lastIndex = prev.length - 1;
      if (lastIndex < 0) return prev;
      // Clear existing ref for this row
      existingRowsRef.current.delete(lastIndex);
      const next = [...prev];
      next[lastIndex] = emptyRow(String(startNo));
      return next;
    });

    // Debounce DB lookup — only call API once after user stops typing
    if (takaNoDebounceRef.current) clearTimeout(takaNoDebounceRef.current);
    takaNoDebounceRef.current = setTimeout(() => {
      const lastIndex = rows.length - 1;
      if (lastIndex >= 0) checkAndPopulateRow(lastIndex, startNo);
    }, 600);
  };

  // Ref map for focusing inputs: key = `${rowIndex}-${fieldName}`
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const setInputRef = useCallback((rowIndex: number, field: string, el: HTMLInputElement | null) => {
    inputRefs.current[`${rowIndex}-${field}`] = el;
  }, []);

  const fieldOrder = useMemo(() => ['meter', 'mcNo', 'qualityDesiNo', 'colour', 'weight', 'avgPick'] as const, []);

  const updateRow = useCallback((index: number, field: keyof TakaEntry, value: string) => {
    if (field === 'meter' && !isMeterValid(value)) return;
    setRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);


  // Save a single row via API
  const saveRowMutation = useMutation({
    mutationFn: async (entry: { rowIndex: number; takaNo: number; series: string | null; date: string; beamNo: string | null; meter: number | null; saree: number | null; mcNo: string | null; qualityDesiNo: string | null; colour: string | null; weight: number | null; avgPick: number | null }) =>
      apiService.post('/taka-inventory', { date: entry.date, beamNo: entry.beamNo, entries: [entry] }),
    onSuccess: (data: any, variables) => {
      // Update the row status to saved
      setRows(prev => {
        const next = [...prev];
        if (next[variables.rowIndex]) {
          next[variables.rowIndex] = { ...next[variables.rowIndex], status: 'saved' };
        }
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['takaInventories'] });
      queryClient.invalidateQueries({ queryKey: ['nextTakaNo'] });
      queryClient.invalidateQueries({ queryKey: ['takaSeriesList'] });
      queryClient.invalidateQueries({ queryKey: ['inStockTakaIds'] });
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to save row')
  });

  // Set to track which rows have already been saved
  const savedRowsRef = useRef<Set<number>>(new Set());

  // Check if there are unsaved rows with meaningful data (not just carried-over quality/colour)
  const hasUnsavedChanges = useCallback(() => {
    if (mode !== 'add') return false;
    return rows.some((r, i) => !savedRowsRef.current.has(i) && (
      (r.meter && parseFloat(r.meter) !== 0) || (r.saree && parseFloat(r.saree) !== 0) || r.mcNo ||
      (r.weight && parseFloat(r.weight) !== 0) || (r.avgPick && parseFloat(r.avgPick) !== 0)
    ));
  }, [mode, rows]);

  // Warn on browser close/refresh if unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // When add mode opens, push a history entry so the browser/header back
  // button pops back into list mode instead of leaving the page.
  useEffect(() => {
    // Create-only users have no list to pop back to, so don't hijack the back button for them.
    if (mode !== 'add' || createOnlyRef.current) return;
    window.history.pushState({ takaAdd: true }, '', window.location.pathname + window.location.search);
    const handlePop = () => setMode('list');
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [mode]);

  // Prefill Taka No when selected series changes (NOT on refetch after save)
  const prevSelectedSeriesRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (prevSelectedSeriesRef.current === selectedSeries) return; // same series, ignore refetches
    if (nextForSelectedSeries === undefined || nextForSelectedSeries === null) return; // wait for data
    prevSelectedSeriesRef.current = selectedSeries;
    // Reset per-row tracking refs — they belonged to the previous series
    savedRowsRef.current = new Set();
    existingRowsRef.current = new Map();
    setFormData(prev => ({ ...prev, startTakaNo: String(nextForSelectedSeries) }));
    setRows(generateRows(INITIAL_ROWS, Number(nextForSelectedSeries)));
  }, [nextForSelectedSeries, selectedSeries]);

  // Handle cancel with unsaved check
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowUnsavedPrompt({ action: 'cancel' });
    } else {
      exitAddMode();
    }
  }, [hasUnsavedChanges, exitAddMode]);

  // Map to track rows loaded from existing DB entries: rowIndex → DB id
  const existingRowsRef = useRef<Map<number, number>>(new Map());

  // Version counter to ignore stale API responses
  const populateVersionRef = useRef(0);

  // Check if a taka number exists in DB and auto-populate the row
  const checkAndPopulateRow = useCallback(async (rowIndex: number, takaNo: number) => {
    const version = ++populateVersionRef.current;
    try {
      const seriesQuery = selectedSeries ? `?series=${encodeURIComponent(selectedSeries)}` : '';
      const data = await apiService.get(`/taka-inventory/by-number/${takaNo}${seriesQuery}`);
      // Ignore stale response if spinner changed since this request
      if (version !== populateVersionRef.current) return;
      if (data?.takaInventory) {
        const t = data.takaInventory;
        const isInStock = inStockTakaIds instanceof Set && inStockTakaIds.has(t.id);
        setRows(prev => {
          const next = [...prev];
          // Verify the row still has the same taka number
          if (next[rowIndex]?.takaNo !== String(takaNo)) return prev;
          // Only set ref after takaNo check passes
          existingRowsRef.current.set(rowIndex, t.id);
          next[rowIndex] = {
            ...next[rowIndex],
            id: t.id,
            meter: String(t.meter ?? ''),
            saree: String(t.saree ?? ''),
            mcNo: t.mcNo || '',
            qualityDesiNo: t.qualityDesiNo || '',
            colour: t.colour || '',
            weight: String(t.weight ?? ''),
            avgPick: String(t.avgPick ?? ''),
            status: isInStock ? 'in_stock' : 'dispatched',
            originalDate: t.date ? String(t.date).split('T')[0] : undefined,
          };
          return next;
        });
      }
    } catch {
      // 404 = taka doesn't exist, nothing to populate
    }
  }, [inStockTakaIds, selectedSeries]);

  // Use ref for formData so moveToNextRow always has latest values
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Track whether the user manually changed the form date in the current add session.
  // When true, manual date overrides any existing row's original date on update.
  const dateUserChangedRef = useRef(false);

  // Move to next row: carry over quality & colour, auto-save current row, increment starting taka no
  const moveToNextRow = useCallback((currentIndex: number) => {
    // Read current rows directly to avoid stale closure
    const currentRows = [...rows];
    const currentRow = currentRows[currentIndex];

    // Auto-save the current row if it has meter data and hasn't been saved yet
    if (currentRow && !isRowEmpty(currentRow) && !savedRowsRef.current.has(currentIndex) && currentRow.meter && parseFloat(currentRow.meter) !== 0) {
      savedRowsRef.current.add(currentIndex);
      const fd = formDataRef.current;
      const takaNo = parseInt(currentRow.takaNo);
      const rowData = {
        meter: currentRow.meter ? parseFloat(currentRow.meter) : null,
        saree: currentRow.saree ? parseFloat(currentRow.saree) : null,
        mcNo: currentRow.mcNo || null,
        qualityDesiNo: currentRow.qualityDesiNo || null,
        colour: currentRow.colour || null,
        weight: currentRow.weight ? parseFloat(currentRow.weight) : null,
        avgPick: currentRow.avgPick ? parseFloat(currentRow.avgPick) : null,
      };

      const existingId = existingRowsRef.current.get(currentIndex);
      if (existingId) {
        // Update existing entry (never update takaNo)
        // Preserve original date unless the user manually changed the form date
        const updateDate = dateUserChangedRef.current ? fd.date : (currentRow.originalDate || fd.date);
        inlineUpdateMutation.mutate({ id: existingId, data: { ...rowData, date: updateDate, beamNo: fd.beamNo || null } });
      } else {
        // Create new entry
        saveRowMutation.mutate({
          rowIndex: currentIndex,
          takaNo,
          series: selectedSeries,
          date: fd.date,
          beamNo: fd.beamNo || null,
          ...rowData,
        });
      }
    }

    // Keep spinner in sync with the next row's taka number
    const nextTakaNo = parseInt(currentRow?.takaNo || '0') + 1;
    if (nextTakaNo > 0) {
      setFormData(fd => ({ ...fd, startTakaNo: String(nextTakaNo) }));
    }

    // Carry over quality & colour to next row, add new row if at end
    setRows(prev => {
      const next = [...prev];
      const row = next[currentIndex];
      const nextIndex = currentIndex + 1;
      // If we're at the last row, add a new empty row
      if (nextIndex >= next.length) {
        const lastNo = parseInt(next[next.length - 1].takaNo) + 1 || next.length + 1;
        const newRow = emptyRow(String(lastNo));
        // Carry over quality, colour, mcNo & avgPick
        if (row?.qualityDesiNo) newRow.qualityDesiNo = row.qualityDesiNo;
        if (row?.colour) newRow.colour = row.colour;
        if (row?.mcNo) newRow.mcNo = row.mcNo;
        if (row?.avgPick) newRow.avgPick = row.avgPick;
        return [...next, newRow];
      }
      // Existing next row — carry over
      if (nextIndex < next.length) {
        const nextRow = next[nextIndex];
        if (!nextRow.qualityDesiNo && row?.qualityDesiNo) {
          next[nextIndex] = { ...nextRow, qualityDesiNo: row.qualityDesiNo };
        }
        if (!next[nextIndex].colour && row?.colour) {
          next[nextIndex] = { ...next[nextIndex], colour: row.colour };
        }
        if (!next[nextIndex].mcNo && row?.mcNo) {
          next[nextIndex] = { ...next[nextIndex], mcNo: row.mcNo };
        }
        if (!next[nextIndex].avgPick && row?.avgPick) {
          next[nextIndex] = { ...next[nextIndex], avgPick: row.avgPick };
        }
      }
      return next;
    });

    // Check if next row's taka number exists in DB and auto-populate
    const nextIndex = currentIndex + 1;
    const nextTakaNoValue = parseInt(rows[currentIndex]?.takaNo || '0') + 1;
    if (nextTakaNoValue > 0 && !savedRowsRef.current.has(nextIndex) && !existingRowsRef.current.has(nextIndex)) {
      checkAndPopulateRow(nextIndex, nextTakaNoValue);
    }

    // Focus first field of next row
    setTimeout(() => {
      const el = inputRefs.current[`${nextIndex}-meter`];
      if (el) el.focus();
    }, 50);
  }, [rows, saveRowMutation, inlineUpdateMutation, checkAndPopulateRow]);

  // Handle keyboard navigation between fields and rows
  const handleRowKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentFieldIdx = fieldOrder.indexOf(field as any);
      if (currentFieldIdx < fieldOrder.length - 1) {
        const nextField = fieldOrder[currentFieldIdx + 1];
        const el = inputRefs.current[`${rowIndex}-${nextField}`];
        if (el) el.focus();
      } else {
        moveToNextRow(rowIndex);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const el = inputRefs.current[`${rowIndex + 1}-${field}`];
      if (el) el.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (rowIndex > 0) {
        const el = inputRefs.current[`${rowIndex - 1}-${field}`];
        if (el) el.focus();
      }
    }
  }, [fieldOrder, moveToNextRow]);

  const removeRow = (index: number) => {
    if (rows.length > 1) {
      // Update savedRowsRef and existingRowsRef indices after removal
      const newSaved = new Set<number>();
      const newExisting = new Map<number, number>();
      savedRowsRef.current.forEach(idx => {
        if (idx < index) newSaved.add(idx);
        else if (idx > index) newSaved.add(idx - 1);
      });
      existingRowsRef.current.forEach((id, idx) => {
        if (idx < index) newExisting.set(idx, id);
        else if (idx > index) newExisting.set(idx - 1, id);
      });
      savedRowsRef.current = newSaved;
      existingRowsRef.current = newExisting;

      const newRows = rows.filter((_, i) => i !== index);
      // Renumber only unsaved rows; saved rows keep their taka number
      let nextNo = parseInt(formData.startTakaNo) || 1;
      setRows(newRows.map((r, i) => {
        if (savedRowsRef.current.has(i) || existingRowsRef.current.has(i)) return r;
        return { ...r, takaNo: String(nextNo++) };
      }));
    }
  };

  const handleBulkSave = () => {
    if (showCompanyColumn && !selectedTenantId) { toast.error('Please select a company'); return; }
    const unsaved = rows
      .map((r, i) => ({ row: r, index: i }))
      .filter(({ row, index }) => !isRowEmpty(row) && !savedRowsRef.current.has(index) && row.meter && parseFloat(row.meter) !== 0);
    if (!unsaved.length) { exitAddMode(); return; }

    // Update existing entries
    unsaved.forEach(({ row, index }) => {
      const existingId = existingRowsRef.current.get(index);
      if (existingId) {
        const updateDate = dateUserChangedRef.current ? formData.date : (row.originalDate || formData.date);
        inlineUpdateMutation.mutate({
          id: existingId,
          data: {
            date: updateDate,
            beamNo: formData.beamNo || null,
            meter: row.meter ? parseFloat(row.meter) : null,
            saree: row.saree ? parseFloat(row.saree) : null,
            mcNo: row.mcNo || null,
            qualityDesiNo: row.qualityDesiNo || null,
            colour: row.colour || null,
            weight: row.weight ? parseFloat(row.weight) : null,
            avgPick: row.avgPick ? parseFloat(row.avgPick) : null,
          }
        });
      }
    });

    // Create new entries (exclude existing ones)
    const newEntries = unsaved.filter(({ index }) => !existingRowsRef.current.has(index));
    if (newEntries.length) {
      bulkCreateMutation.mutate({
        date: formData.date,
        beamNo: formData.beamNo || null,
        tenantId: showCompanyColumn ? selectedTenantId : undefined,
        entries: newEntries.map(({ row }) => ({
          takaNo: parseInt(row.takaNo),
          series: selectedSeries,
          meter: row.meter ? parseFloat(row.meter) : null,
          saree: row.saree ? parseFloat(row.saree) : null,
          mcNo: row.mcNo || null,
          qualityDesiNo: row.qualityDesiNo || null,
          colour: row.colour || null,
          weight: row.weight ? parseFloat(row.weight) : null,
          avgPick: row.avgPick ? parseFloat(row.avgPick) : null,
        }))
      });
    } else {
      // All were updates, just leave add mode (list for viewers, fresh form for create-only)
      queryClient.invalidateQueries({ queryKey: ['takaInventories'] });
      exitAddMode();
      toast.success('Taka entries updated successfully!');
    }
  };

  const handleEditSave = () => {
    if (!editingId) return;
    updateMutation.mutate({
      id: editingId,
      data: {
        date: editData.date || undefined,
        beamNo: editData.beamNo || null,
        meter: editData.meter ? parseFloat(editData.meter) : null,
        saree: editData.saree ? parseFloat(editData.saree) : null,
        mcNo: editData.mcNo || null,
        qualityDesiNo: editData.qualityDesiNo || null,
        colour: editData.colour || null,
        weight: editData.weight ? parseFloat(editData.weight) : null,
        avgPick: editData.avgPick ? parseFloat(editData.avgPick) : null,
      }
    });
  };

  const filledCount = rows.filter(r => !isRowEmpty(r)).length;
  const totalMeter = rows.reduce((s, r) => s + (parseFloat(r.meter) || 0), 0);
  const totalWeight = rows.reduce((s, r) => s + (parseFloat(r.weight) || 0), 0);

  // ADD BULK ENTRY
  if (mode === 'add' || createOnly) {
    return (
      <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="px-0 sm:px-2 py-3 space-y-3 pb-24 md:pb-3">
        {/* Stats + Form Bar */}
        <div className="bg-white rounded-xl border border-[#E8EBF1] p-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:items-center sm:gap-3 sm:flex-1 sm:min-w-0 sm:flex-wrap">
              <div className="flex items-center gap-2 bg-orange-50 border border-[#E1E6EF] rounded-lg px-3 py-1.5">
                <div className="w-7 h-7 rounded-md bg-orange-100 flex items-center justify-center">
                  <span className="text-orange-600 text-xs font-black">#</span>
                </div>
                <div>
                  <p className="text-xs text-orange-400 font-semibold uppercase leading-none">Taka No</p>
                  <input type="text" inputMode="numeric" value={formData.startTakaNo}
                    onChange={e => handleStartTakaNoChange(e.target.value)}
                    style={{ width: `${Math.max(3, String(formData.startTakaNo).length + 2)}ch` }}
                    className="text-sm font-bold text-orange-700 bg-transparent border-0 p-0 focus:outline-none focus:ring-0" />
                </div>
              </div>
              {/* Series picker */}
              <div className="flex items-center gap-2 bg-slate-50 border border-[#E1E6EF] rounded-lg px-3 py-1.5 min-w-0">
                <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-slate-600 text-xs font-black">S</span>
                </div>
                <div className="min-w-0 sm:flex sm:items-center sm:gap-2">
                  <p className="text-xs text-slate-400 font-semibold uppercase leading-none">Series</p>
                  {!showNewSeriesInput ? (
                    <select
                      value={selectedSeries === null ? '__default__' : selectedSeries}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__new__') {
                          setShowNewSeriesInput(true);
                          setNewSeriesInput('');
                        } else if (v === '__default__') {
                          setSelectedSeries(null);
                        } else {
                          setSelectedSeries(v);
                        }
                      }}
                      className="max-w-full text-sm font-bold text-slate-700 bg-transparent border-0 p-0 focus:outline-none focus:ring-0"
                    >
                      {seriesList.map((s: { series: string | null; nextTakaNo: number; count: number }) => (
                        <option key={s.series ?? '__default__'} value={s.series === null ? '__default__' : s.series}>
                          {seriesLabel(s.series)}
                        </option>
                      ))}
                      {selectedSeries && !seriesList.some((s: { series: string | null }) => s.series === selectedSeries) && (
                        <option value={selectedSeries}>{selectedSeries} (new)</option>
                      )}
                      <option value="__new__">+ New series</option>
                    </select>
                  ) : (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        autoFocus
                        value={newSeriesInput}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 5).toUpperCase();
                          setNewSeriesInput(cleaned);
                          if (cleaned) setSelectedSeries(cleaned);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); setShowNewSeriesInput(false); }
                          if (e.key === 'Escape') { setShowNewSeriesInput(false); setNewSeriesInput(''); setSelectedSeries(null); }
                        }}
                        onBlur={() => { if (newSeriesInput.trim()) setShowNewSeriesInput(false); }}
                        placeholder="Prefix"
                        className="text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded px-2 py-0.5 w-16 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                      <button
                        type="button"
                        onClick={() => { setShowNewSeriesInput(false); setNewSeriesInput(''); setSelectedSeries(null); }}
                        className="text-xs text-slate-400 hover:text-slate-600"
                        title="Cancel"
                      >×</button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <p className="text-xs text-blue-400 font-semibold uppercase leading-none">Meter</p>
                  <p className="text-lg font-bold text-blue-700 leading-tight">{totalMeter.toFixed(2)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-purple-50 rounded-lg px-3 py-2 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" /></svg>
                </div>
                <div>
                  <p className="text-xs text-purple-400 font-semibold uppercase leading-none">Weight</p>
                  <p className="text-lg font-bold text-purple-700 leading-tight">{totalWeight.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Form fields + Cancel */}
            <div className="flex flex-row items-end gap-2 w-full sm:w-auto">
              {showCompanyColumn && (
                <div className="flex-1 sm:flex-none sm:w-40">
                  <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-0.5">COMPANY</label>
                  <CompanySelect value={selectedTenantId} onChange={setSelectedTenantId} />
                </div>
              )}
              <div className="flex-1 sm:flex-none">
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-0.5">BEAM NO</label>
                <input type="text" value={formData.beamNo} onChange={e => setFormData({ ...formData, beamNo: e.target.value })}
                  className="w-full sm:w-24 px-2.5 py-2 sm:py-1.5 text-sm font-semibold bg-white border border-[#E1E6EF] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" placeholder="Enter" />
              </div>
              <div className="flex-1 sm:flex-none">
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-0.5">DATE</label>
                <input type="date" value={formData.date} onChange={e => { dateUserChangedRef.current = true; setFormData({ ...formData, date: e.target.value }); }}
                  className="w-full sm:w-auto px-2.5 py-2 sm:py-1.5 text-sm bg-white border border-[#E1E6EF] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
              </div>
              <button onClick={handleCancel} className="hidden sm:block px-3 py-[7px] text-sm font-medium text-gray-600 bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] shadow-[1px_1px_3px_rgba(0,0,0,0.06)] transition-colors">Cancel</button>
            </div>
          </div>
        </div>

        {/* Entry cards — mobile. Rendered instead of (not alongside) the table so
            inputRefs point at the visible inputs and keyboard nav stays correct. */}
        {isMobile && <div className="space-y-2">
          {rows.map((row, i) => {
            const filled = !isRowEmpty(row);
            const isExisting = existingRowsRef.current.has(i);
            const isSaved = savedRowsRef.current.has(i);
            const rowStatus = isExisting ? (row.status || 'in_stock') : isSaved ? 'saved' : 'pending';
            const isLast = i === rows.length - 1;
            const fi = "w-full px-3 py-2 text-sm bg-white border border-[#E1E6EF] rounded-lg focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20";
            const lbl = "block text-[11px] font-semibold text-[rgba(14,19,27,0.55)] uppercase tracking-wide mb-1";
            return (
              <div key={i} className={`rounded-xl border p-3 ${
                isExisting ? 'bg-amber-50/60 border-amber-200'
                  : isSaved ? 'bg-green-50/50 border-green-200'
                  : filled ? 'bg-white border-[#0055a9]/30' : 'bg-white border-[#E8EBF1]'}`}>
                {/* Card header */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base font-bold text-[#0055a9]">Taka #{row.takaNo}</span>
                  {rowStatus === 'pending' && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Pending</span>}
                  {(rowStatus === 'saved' || rowStatus === 'in_stock') && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">In Stock</span>}
                  {rowStatus === 'dispatched' && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Dispatched</span>}
                  {filled && !isExisting && (
                    <button type="button" onClick={() => removeRow(i)} aria-label={`Remove taka ${row.takaNo}`}
                      className="ml-auto p-2 -mr-1 rounded-lg text-[rgba(14,19,27,0.4)] hover:text-[#E02D3C] hover:bg-red-50" tabIndex={-1}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className={lbl}>Meter</label>
                    <input type="text" inputMode="decimal" value={row.meter} onChange={e => updateRow(i, 'meter', e.target.value)}
                      ref={el => setInputRef(i, 'meter', el)} onKeyDown={e => handleRowKeyDown(e, i, 'meter')}
                      className={fi} placeholder="0.00" />
                  </div>
                  <div>
                    <label className={lbl}>MC No</label>
                    <div className="[&_input]:!px-3 [&_input]:!py-2 [&_input]:!bg-white [&_input]:!border-[#E1E6EF] [&_input]:!rounded-lg">
                      <McComboBox value={row.mcNo} onChange={v => updateRow(i, 'mcNo', v)} machines={machinesList}
                        inputRef={el => setInputRef(i, 'mcNo', el)}
                        onKeyDown={e => handleRowKeyDown(e, i, 'mcNo')} />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className={lbl}>Quality</label>
                    <div className="bg-white border border-[#E1E6EF] rounded-lg [&_input]:!px-3 [&_input]:!py-2">
                      <QualityDropdown value={row.qualityDesiNo} onChange={v => updateRow(i, 'qualityDesiNo', v)}
                        qualities={qualities} onCreateQuality={n => createQualityMutation.mutate(n)}
                        onSelectQuality={q => {
                          if (q.colour) updateRow(i, 'colour', q.colour);
                          if (q.pick != null) updateRow(i, 'avgPick', String(q.pick));
                        }}
                        inputRef={el => setInputRef(i, 'qualityDesiNo', el)}
                        onEnterCommit={() => inputRefs.current[`${i}-colour`]?.focus()} />
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Colour</label>
                    <input type="text" value={row.colour} onChange={e => updateRow(i, 'colour', e.target.value)}
                      ref={el => setInputRef(i, 'colour', el)} onKeyDown={e => handleRowKeyDown(e, i, 'colour')}
                      className={fi} placeholder="-" />
                  </div>
                  <div>
                    <label className={lbl}>Weight</label>
                    <input type="number" step="0.01" inputMode="decimal" value={row.weight} onChange={e => updateRow(i, 'weight', e.target.value)}
                      ref={el => setInputRef(i, 'weight', el)} onKeyDown={e => handleRowKeyDown(e, i, 'weight')}
                      className={fi} placeholder="0.00" />
                  </div>
                  <div>
                    <label className={lbl}>Avg. Pick</label>
                    <input type="number" step="0.01" inputMode="decimal" value={row.avgPick} onChange={e => updateRow(i, 'avgPick', e.target.value)}
                      ref={el => setInputRef(i, 'avgPick', el)} onKeyDown={e => handleRowKeyDown(e, i, 'avgPick')}
                      className={fi} placeholder="0.00" />
                  </div>
                </div>

                {isLast && (
                  <button type="button" onClick={() => moveToNextRow(i)}
                    disabled={!row.meter || parseFloat(row.meter) === 0}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-[#0055a9] bg-[#0055a9]/5 border border-[#0055a9]/25 rounded-xl active:bg-[#0055a9]/10 disabled:opacity-40 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Save &amp; Next Taka
                  </button>
                )}
              </div>
            );
          })}

          {/* Mobile totals */}
          <div className="flex items-center justify-between rounded-xl bg-[#F8F9FC] border border-[#E8EBF1] px-4 py-3">
            <span className="text-xs font-bold uppercase tracking-wide text-[rgba(14,19,27,0.65)]">Total</span>
            <div className="flex items-center gap-4">
              <span className="text-sm"><span className="text-[rgba(14,19,27,0.5)]">Meter </span><span className="font-bold text-blue-700 tabular-nums">{totalMeter.toFixed(2)}</span></span>
              <span className="text-sm"><span className="text-[rgba(14,19,27,0.5)]">Weight </span><span className="font-bold text-purple-700 tabular-nums">{totalWeight.toFixed(2)}</span></span>
            </div>
          </div>
        </div>}

        {/* Table — desktop */}
        {!isMobile && <div className="bg-white rounded-xl border border-[#E8EBF1] overflow-hidden mb-4">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-y border-[#F1F3F9]">
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider w-14">TAKA#</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider">Meter</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider w-16">MC</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider min-w-[240px]">Quality</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider">Colour</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider">Weight</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider">Avg. Pick</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC] tracking-wider w-24">Status</th>
                  <th className="px-1 py-2 w-8 bg-[#F8F9FC]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => {
                  const filled = !isRowEmpty(row);
                  const isExisting = existingRowsRef.current.has(i);
                  const isSaved = savedRowsRef.current.has(i);
                  const rowStatus = isExisting ? (row.status || 'in_stock') : isSaved ? 'saved' : 'pending';
                  const rowBg = isExisting ? 'bg-amber-50/50' : isSaved ? 'bg-green-50/30' : filled ? 'bg-blue-50/30' : '';
                  return (
                    <tr key={i} className={rowBg}>
                      <td className="px-2 py-1 text-center text-sm font-semibold text-[#0055a9] border-r border-gray-100">{row.takaNo}</td>
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="text" inputMode="decimal" value={row.meter} onChange={e => updateRow(i, 'meter', e.target.value)}
                          ref={el => setInputRef(i, 'meter', el)} onKeyDown={e => handleRowKeyDown(e, i, 'meter')}
                          className="w-full px-2 py-1 text-sm border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent" placeholder="0.00" />
                      </td>
                      <td className="px-1 py-1 w-16 border-r border-gray-100">
                        <McComboBox
                          value={row.mcNo}
                          onChange={v => updateRow(i, 'mcNo', v)}
                          machines={machinesList}
                          inputRef={el => setInputRef(i, 'mcNo', el)}
                          onKeyDown={e => handleRowKeyDown(e, i, 'mcNo')}
                        />
                      </td>
                      <td className="px-1 py-1 min-w-[240px] border-r border-gray-100">
                        <div className="border border-transparent rounded-lg hover:border-[#E1E6EF]">
                          <QualityDropdown value={row.qualityDesiNo} onChange={v => updateRow(i, 'qualityDesiNo', v)}
                            qualities={qualities} onCreateQuality={n => createQualityMutation.mutate(n)}
                            onSelectQuality={q => {
                              if (q.colour) updateRow(i, 'colour', q.colour);
                              if (q.pick != null) updateRow(i, 'avgPick', String(q.pick));
                            }}
                            inputRef={el => setInputRef(i, 'qualityDesiNo', el)}
                            onEnterCommit={() => {
                              const el = inputRefs.current[`${i}-colour`];
                              if (el) el.focus();
                            }} />
                        </div>
                      </td>
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="text" value={row.colour} onChange={e => updateRow(i, 'colour', e.target.value)}
                          ref={el => setInputRef(i, 'colour', el)} onKeyDown={e => handleRowKeyDown(e, i, 'colour')}
                          className="w-full px-2 py-1 text-sm border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent" placeholder="-" />
                      </td>
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="number" step="0.01" value={row.weight} onChange={e => updateRow(i, 'weight', e.target.value)}
                          ref={el => setInputRef(i, 'weight', el)} onKeyDown={e => handleRowKeyDown(e, i, 'weight')}
                          className="w-full px-2 py-1 text-sm border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent" placeholder="0.00" />
                      </td>
                      <td className="px-1 py-1 border-r border-gray-100">
                        <input type="number" step="0.01" value={row.avgPick} onChange={e => updateRow(i, 'avgPick', e.target.value)}
                          ref={el => setInputRef(i, 'avgPick', el)} onKeyDown={e => handleRowKeyDown(e, i, 'avgPick')}
                          className="w-full px-2 py-1 text-sm border border-transparent rounded-lg hover:border-[#E1E6EF] focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 focus:bg-white bg-transparent" placeholder="0.00" />
                      </td>
                      <td className="px-1 py-1 text-center">
                        {rowStatus === 'pending' && <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Pending</span>}
                        {rowStatus === 'saved' && <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">In Stock</span>}
                        {rowStatus === 'in_stock' && <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">In Stock</span>}
                        {rowStatus === 'dispatched' && <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Dispatched</span>}
                      </td>
                      <td className="px-1 py-1 text-center">
                        {filled && !isExisting && (
                          <button type="button" onClick={() => removeRow(i)} className="text-[rgba(14,19,27,0.4)] hover:text-[#E02D3C] hover:bg-red-50 p-0.5 rounded" tabIndex={-1}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-y border-[#F1F3F9] bg-[#F8F9FC]">
                  <td className="px-3 py-2 text-xs font-bold text-[rgba(14,19,27,0.65)]">TOTAL</td>
                  <td className="px-3 py-2 text-sm font-bold text-blue-700 text-center tabular-nums">{totalMeter.toFixed(2)}</td>
                  <td className="px-3 py-2" colSpan={3}></td>
                  <td className="px-3 py-2 text-sm font-bold text-purple-700 text-center tabular-nums">{totalWeight.toFixed(2)}</td>
                  <td className="px-3 py-2" colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>}

        {/* Sticky action bar — mobile only. Desktop saves via Enter / the unsaved prompt. */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 flex items-center gap-2 px-4 py-3 bg-white/95 backdrop-blur border-t border-[#E8EBF1] pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button onClick={handleCancel}
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-[#E1E6EF] rounded-xl active:bg-[#F8F9FC] transition-colors">
            Cancel
          </button>
          <button onClick={handleBulkSave}
            className="flex-[2] px-4 py-2.5 text-sm font-semibold text-white bg-[#0055a9] rounded-xl active:bg-[#0d2780] transition-colors">
            Save &amp; Close ({filledCount})
          </button>
        </div>

        {/* Unsaved Changes Prompt */}
        {showUnsavedPrompt && (
          <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            tabIndex={-1} ref={el => el?.focus()}
            onKeyDown={e => { if (e.key === 'Escape') setShowUnsavedPrompt(null); if (e.key === 'Enter') { e.preventDefault(); setShowUnsavedPrompt(null); handleBulkSave(); } }}>
            <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FCEDB9] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#A82C00]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1B202A]">Unsaved Changes</h3>
                  <p className="text-sm text-gray-500">You have unsaved taka entries</p>
                </div>
              </div>
              <p className="text-sm text-[#0E131B] mb-5">
                Some entries have not been saved. Do you want to save them before leaving?
              </p>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => { setShowUnsavedPrompt(null); exitAddMode(); }}
                  className="px-4 py-2 text-sm font-semibold text-red-600 bg-white border border-gray-200 rounded-xl hover:bg-red-50 transition-colors">
                  Discard
                </button>
                <button onClick={() => setShowUnsavedPrompt(null)}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                  Go Back
                </button>
                <button onClick={() => { setShowUnsavedPrompt(null); handleBulkSave(); }}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] transition-colors">
                  Save & Leave
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    );
  }

  // LIST VIEW
  const listTotalMeter = takaList.reduce((s: number, t: any) => s + (parseFloat(t.meter) || 0), 0);
  const listTotalWeight = takaList.reduce((s: number, t: any) => s + (parseFloat(t.weight) || 0), 0);

  const totalPages = pagination.totalPages;
  const totalRecords = pagination.total;
  const activeFilterCount = filterQuality.length + filterColour.length + filterSeries.length + (filterDate ? 1 : 0);

  const monthLabel = filterMonth ? new Date(filterMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : 'All';

  return (
    <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="px-0 sm:px-2 py-3 sm:py-4">
      {/* Single white card container */}
      <div className="bg-white rounded-xl border border-[#E8EBF1]">
        {/* Header bar */}
        <div className="flex items-center flex-wrap gap-2 sm:gap-3 px-3 py-3 sm:px-5 sm:py-4 border-b border-[#E8EBF1]">
          <h1 className="text-xl sm:text-2xl font-bold text-[#1B202A] mr-auto">Taka Inventory</h1>

          {/* Filter toggle — mobile only */}
          <button onClick={() => setShowFilters(v => !v)} aria-expanded={showFilters}
            className={`sm:hidden inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold border transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'bg-[#0055a9]/5 border-[#0055a9]/30 text-[#0055a9]'
                : 'bg-white border-[#E1E6EF] text-[rgba(14,19,27,0.8)]'}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L15 12.414V19a1 1 0 01-.553.894l-4 2A1 1 0 019 21v-8.586L3.293 6.707A1 1 0 013 6V4z" /></svg>
            {activeFilterCount > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#0055a9] text-white text-[11px] font-bold leading-[18px] text-center">{activeFilterCount}</span>}
          </button>

          {/* New Entry button — first row on mobile, trailing on ≥sm */}
          <button onClick={openAdd} disabled={!canWrite}
            title={!canWrite ? 'You have read-only access' : undefined}
            className="sm:order-last inline-flex items-center gap-1.5 bg-[#0055a9] text-white rounded-xl px-3 sm:px-4 py-2 text-sm font-semibold hover:bg-[#0d2780] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            <span className="sm:hidden">New</span>
            <span className="hidden sm:inline">New Entry</span>
          </button>

          {/* Taka grouping feature toggle (per-user setting) */}
          <button onClick={() => setGroupsEnabledMutation.mutate(!takaGroupsEnabled)}
            title={takaGroupsEnabled ? 'Taka grouping is on — click to turn off' : 'Turn on taka grouping'}
            className={`sm:order-last inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold border transition-colors ${
              takaGroupsEnabled ? 'bg-[#0055a9]/5 border-[#0055a9]/30 text-[#0055a9]' : 'bg-white border-[#E1E6EF] text-[rgba(14,19,27,0.7)] hover:bg-[#F8F9FC]'}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.7} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" /></svg>
            <span className="hidden sm:inline">Groups</span>
          </button>

          {/* Search */}
          <div className="relative w-full sm:w-auto">
            <svg className="w-4 h-4 text-[rgba(14,19,27,0.4)] absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input type="text" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} placeholder="Search quality, machine, colour..."
              className="pl-9 pr-3 py-2 text-sm bg-[#F8F9FC] border border-[#E1E6EF] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] w-full sm:w-72" />
          </div>

          {/* Filters — collapsed behind the toggle on mobile; `sm:contents` restores
              them as direct flex children of the header row on ≥sm. */}
          <div className={`${showFilters ? 'grid' : 'hidden'} w-full grid-cols-2 gap-2 sm:contents`}>
            {/* Quality filter */}
            <SearchableSelect value={filterQuality} onChange={v => { setFilterQuality(v); setPage(1); }} options={distinctQualities} placeholder="All Qualities"
              className="px-3 py-1.5 text-sm bg-white border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] focus-within:ring-2 focus-within:ring-[#0055a9]/20 focus-within:border-[#0055a9] w-full sm:w-52 cursor-text flex items-center gap-1 flex-wrap min-h-[40px] sm:min-h-[36px]" />

            {/* Colour filter */}
            <SearchableSelect value={filterColour} onChange={v => { setFilterColour(v); setPage(1); }} options={distinctColours} placeholder="All Colors"
              className="px-3 py-1.5 text-sm bg-white border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] focus-within:ring-2 focus-within:ring-[#0055a9]/20 focus-within:border-[#0055a9] w-full sm:w-52 cursor-text flex items-center gap-1 flex-wrap min-h-[40px] sm:min-h-[36px]" />

            {/* Series filter */}
            <SearchableSelect value={filterSeries} onChange={v => { setFilterSeries(v); setPage(1); }} options={seriesList.map((s: { series: string | null }) => s.series === null ? 'Default' : s.series)} placeholder="All Series"
              className="px-3 py-1.5 text-sm bg-white border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] focus-within:ring-2 focus-within:ring-[#0055a9]/20 focus-within:border-[#0055a9] w-full sm:w-44 cursor-text flex items-center gap-1 flex-wrap min-h-[40px] sm:min-h-[36px]" />

            {/* Date filter */}
            <input type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); if (e.target.value) { setFilterMonth(e.target.value.slice(0, 7)); } setPage(1); }}
              className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm bg-white border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />

            {/* Month filter */}
            <input type="month" value={filterMonth} onChange={e => { setFilterMonth(e.target.value); setFilterDate(''); setPage(1); }}
              className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm bg-white border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />

            {/* Clear filters */}
            {activeFilterCount > 0 && (
              <button onClick={() => { setFilterQuality([]); setFilterColour([]); setFilterSeries([]); setFilterDate(''); setPage(1); }}
                className="col-span-2 sm:col-auto py-2 sm:py-0 text-xs text-[#0055a9] hover:text-[#0d2780] font-medium">
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Taka groups + ungrouped label — only when the grouping feature is enabled in settings */}
        {takaGroupsEnabled && (
          <div className="px-4 sm:px-6 pt-4">
            <TakaGroupsPanel canWrite={canWrite} seriesOptions={seriesList.map((s: any) => s.series)} />
            <h3 className="text-sm font-bold text-[#1B202A]">Ungrouped</h3>
          </div>
        )}

        {/* Table content */}
        {isLoading ? (
          <div className="p-16 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0055a9] border-t-transparent mx-auto mb-3"></div>
            <p className="text-sm text-[rgba(14,19,27,0.5)]">Loading...</p>
          </div>
        ) : takaList.length > 0 ? (
          <>
            {/* Record cards — mobile */}
            {isMobile && <div className="divide-y divide-[#F1F3F9]">
              {takaList.map((t: any, i: number) => {
                const isEditing = editingId === t.id;
                const isInStock = inStockTakaIds instanceof Set && inStockTakaIds.has(t.id);
                const eic = "w-full text-sm border border-[#E1E6EF] rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] bg-white";
                const lbl = "block text-[11px] font-semibold text-[rgba(14,19,27,0.45)] uppercase tracking-wide mb-0.5";
                const val = (v: any) => v || v === 0 ? <span className="text-sm font-medium text-[#0E131B]">{v}</span> : <span className="text-sm text-[rgba(14,19,27,0.3)]">-</span>;
                const showGroupHeader = showCompanyColumn && t.tenantId !== (takaList[i - 1]?.tenantId);
                return (
                  <React.Fragment key={t.id}>
                  {showGroupHeader && (
                    <div className="px-4 py-2 bg-[#EEF2F8] text-sm font-bold text-[#0E131B] border-y border-[#DDE3EA]">
                      <CompanyCell tenantId={t.tenantId} />
                    </div>
                  )}
                  <div className={`px-4 py-3 ${isEditing ? 'bg-[#FFFBEB]' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-bold text-[#0055a9]">{formatTakaNo(t.series, t.takaNo)}</span>
                      {stockPending ? (
                        <span aria-label="Loading status" className="inline-block w-[74px] h-[20px] rounded-full bg-[#F1F3F9] border border-[#E8EBF1] animate-pulse" />
                      ) : (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                          isInStock ? 'bg-[#D7F7C2] border-[#A6EB84] text-[#006908]' : 'bg-[#FCEDB9] border-[#FCD579] text-[#A82C00]'}`}>
                          {isInStock ? 'In Stock' : 'Dispatched'}
                        </span>
                      )}
                      <span className="ml-auto flex items-center gap-1">
                        {isEditing ? (
                          <>
                            <button onClick={handleEditSave} disabled={updateMutation.isPending}
                              className="p-2 rounded-lg text-[#006908] bg-[#D7F7C2]/50 active:bg-[#D7F7C2]" aria-label="Save">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            </button>
                            <button onClick={cancelEdit} className="p-2 rounded-lg text-[rgba(14,19,27,0.5)] bg-[#F8F9FC]" aria-label="Cancel">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => openEdit(t)} disabled={!canWrite} title={!canWrite ? 'You have read-only access' : undefined} className="p-2 rounded-lg text-[rgba(14,19,27,0.7)] active:bg-[#F8F9FC] disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Edit entry">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            </button>
                            <button onClick={() => setDeletingTakaId(t.id)} disabled={!canWrite} title={!canWrite ? 'You have read-only access' : undefined} className="p-2 rounded-lg text-[#E02D3C] active:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Delete entry">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </>
                        )}
                      </span>
                    </div>

                    {isEditing ? (
                      <div className="grid grid-cols-2 gap-2.5 mt-3">
                        <div><label className={lbl}>Date</label><input type="date" value={editData.date} onChange={e => setEditData({ ...editData, date: e.target.value })} className={eic} /></div>
                        <div><label className={lbl}>Beam</label><input type="text" value={editData.beamNo} onChange={e => setEditData({ ...editData, beamNo: e.target.value })} className={eic} /></div>
                        <div className="col-span-2">
                          <label className={lbl}>Quality</label>
                          <div className="border border-[#E1E6EF] rounded-lg bg-white [&_input]:!px-2.5 [&_input]:!py-2">
                            <QualityDropdown value={editData.qualityDesiNo} onChange={v => setEditData({ ...editData, qualityDesiNo: v })} qualities={qualities} onCreateQuality={n => createQualityMutation.mutate(n)} />
                          </div>
                        </div>
                        <div><label className={lbl}>MC</label><input type="text" value={editData.mcNo} onChange={e => setEditData({ ...editData, mcNo: e.target.value })} className={eic} /></div>
                        <div><label className={lbl}>Colour</label><input type="text" value={editData.colour} onChange={e => setEditData({ ...editData, colour: e.target.value })} className={eic} /></div>
                        <div><label className={lbl}>Meter</label><input type="text" inputMode="decimal" value={editData.meter} onChange={e => { if (isMeterValid(e.target.value)) setEditData({ ...editData, meter: e.target.value }); }} className={eic} /></div>
                        <div><label className={lbl}>Weight</label><input type="number" step="0.01" inputMode="decimal" value={editData.weight} onChange={e => setEditData({ ...editData, weight: e.target.value })} className={eic} /></div>
                        <div><label className={lbl}>Pick</label><input type="number" step="0.01" inputMode="decimal" value={editData.avgPick} onChange={e => setEditData({ ...editData, avgPick: e.target.value })} className={eic} /></div>
                      </div>
                    ) : (
                      <>
                        <div className="mt-1.5 text-sm text-[#0E131B]">
                          {t.qualityDesiNo || <span className="text-[rgba(14,19,27,0.3)]">No quality</span>}
                          <span className="text-[rgba(14,19,27,0.45)]"> · {new Date(t.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2.5">
                          {([
                            { label: 'Meter', value: t.meter, accent: true },
                            { label: 'Weight', value: t.weight },
                            { label: 'Pick', value: t.avgPick },
                            { label: 'Colour', value: t.colour },
                            { label: 'Beam', value: t.beamNo },
                            { label: 'MC', value: t.mcNo },
                          ] as { label: string; value: any; accent?: boolean }[]).map((f) => {
                            const has = f.value || f.value === 0;
                            return (
                              <div key={f.label} className="rounded-lg bg-[#F8F9FC] px-2 py-1.5 text-center min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-[rgba(14,19,27,0.4)] leading-none">{f.label}</p>
                                <p className={`mt-1 text-sm font-bold tabular-nums truncate ${has ? (f.accent ? 'text-emerald-700' : 'text-[#0E131B]') : 'text-[rgba(14,19,27,0.3)]'}`}>{has ? f.value : '-'}</p>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                  </React.Fragment>
                );
              })}

              {/* Mobile totals */}
              <div className="flex items-center justify-between bg-[#F8F9FC] px-4 py-3">
                <span className="text-xs font-bold uppercase tracking-wide text-[rgba(14,19,27,0.65)]">Total</span>
                <div className="flex items-center gap-4">
                  <span className="text-sm"><span className="text-[rgba(14,19,27,0.5)]">Meter </span><span className="font-bold text-[#0E131B] tabular-nums">{listTotalMeter.toFixed(2)}</span></span>
                  <span className="text-sm"><span className="text-[rgba(14,19,27,0.5)]">Weight </span><span className="font-bold text-[#0E131B] tabular-nums">{listTotalWeight.toFixed(2)}</span></span>
                </div>
              </div>
            </div>}

            {!isMobile && <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="z-10 shadow-[0_1px_0_0_#E8EBF1]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase w-14 bg-[#F8F9FC]">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Quality</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Beam</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">MC</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Meter</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Colour</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Weight</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Pick</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Status</th>
                    {showCompanyColumn && <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Company</th>}
                    <th className="px-4 py-3 w-12 bg-[#F8F9FC]"></th>
                  </tr>
                </thead>
                <tbody>
                  {takaList.map((t: any, i: number) => {
                    const isEditing = editingId === t.id;
                    const isInStock = inStockTakaIds instanceof Set && inStockTakaIds.has(t.id);
                    const eic = "w-full text-sm border border-[#E1E6EF] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] bg-white";
                    // In the multi-company view, start a company block whenever the tenant changes.
                    const showGroupHeader = showCompanyColumn && t.tenantId !== (takaList[i - 1]?.tenantId);
                    return (
                      <React.Fragment key={t.id}>
                      {showGroupHeader && (
                        <tr className="bg-[#EEF2F8]">
                          <td colSpan={12} className="px-4 py-2.5 text-sm font-bold text-[#0E131B] border-y border-[#DDE3EA]">
                            <CompanyCell tenantId={t.tenantId} />
                          </td>
                        </tr>
                      )}
                      <motion.tr initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className={`border-b border-[#F1F3F9] transition-colors ${isEditing ? 'bg-[#FFFBEB]' : 'hover:bg-[#FAFBFD]'}`}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-[#0E131B]">{formatTakaNo(t.series, t.takaNo)}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {isEditing ? <input type="date" value={editData.date} onChange={e => setEditData({ ...editData, date: e.target.value })} className={eic + ' w-24 sm:w-32'} />
                            : <span className="text-sm text-[#0E131B]">{new Date(t.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {isEditing ? <div className="min-w-[160px] border border-[#E1E6EF] rounded-lg"><QualityDropdown value={editData.qualityDesiNo} onChange={v => setEditData({ ...editData, qualityDesiNo: v })} qualities={qualities} onCreateQuality={n => createQualityMutation.mutate(n)} /></div>
                            : t.qualityDesiNo ? <span className="text-sm text-[#0E131B]">{t.qualityDesiNo}</span> : <span className="text-sm text-[rgba(14,19,27,0.3)]">-</span>}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {isEditing ? <input type="text" value={editData.beamNo} onChange={e => setEditData({ ...editData, beamNo: e.target.value })} className={eic + ' w-12 sm:w-16'} />
                            : <span className="text-sm text-[#0E131B]">{t.beamNo || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</span>}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {isEditing ? <input type="text" value={editData.mcNo} onChange={e => setEditData({ ...editData, mcNo: e.target.value })} className={eic + ' w-12 sm:w-16'} />
                            : <span className="text-sm text-[#0E131B]">{t.mcNo || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</span>}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          {isEditing ? <input type="text" inputMode="decimal" value={editData.meter}
                            onChange={e => { if (isMeterValid(e.target.value)) setEditData({ ...editData, meter: e.target.value }); }}
                            className={eic + ' w-16 sm:w-20 text-right'} />
                            : <span className="text-sm font-semibold text-[#0E131B] tabular-nums">{t.meter || <span className="text-[rgba(14,19,27,0.3)] font-normal">-</span>}</span>}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {isEditing ? <input type="text" value={editData.colour} onChange={e => setEditData({ ...editData, colour: e.target.value })} className={eic + ' w-12 sm:w-16'} />
                            : <span className="text-sm text-[#0E131B]">{t.colour || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</span>}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          {isEditing ? <input type="number" step="0.01" value={editData.weight} onChange={e => setEditData({ ...editData, weight: e.target.value })} className={eic + ' w-16 sm:w-20 text-right'} />
                            : <span className="text-sm font-semibold text-[#0E131B] tabular-nums">{t.weight || <span className="text-[rgba(14,19,27,0.3)] font-normal">-</span>}</span>}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          {isEditing ? <input type="number" step="0.01" value={editData.avgPick} onChange={e => setEditData({ ...editData, avgPick: e.target.value })} className={eic + ' w-12 sm:w-16 text-right'} />
                            : <span className="text-sm text-[#0E131B] tabular-nums">{t.avgPick || <span className="text-[rgba(14,19,27,0.3)]">-</span>}</span>}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {stockPending ? (
                            <span aria-label="Loading status" className="inline-block w-[86px] h-[26px] rounded-full bg-[#F1F3F9] border border-[#E8EBF1] animate-pulse" />
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${
                              isInStock
                                ? 'bg-[#D7F7C2] border-[#A6EB84] text-[#006908]'
                                : 'bg-[#FCEDB9] border-[#FCD579] text-[#A82C00]'
                            }`}>
                              {isInStock ? 'In Stock' : 'Dispatched'}
                            </span>
                          )}
                        </td>
                        {showCompanyColumn && (
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-[#0E131B]">
                            <CompanyCell tenantId={t.tenantId} />
                          </td>
                        )}
                        <td className="px-4 py-4 text-right">
                          {isEditing ? (
                            <div className="inline-flex items-center gap-1">
                              <button onClick={handleEditSave} disabled={updateMutation.isPending}
                                className="p-1.5 rounded-lg text-[#006908] hover:bg-[#D7F7C2]/50 transition-colors" title="Save">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              </button>
                              <button onClick={cancelEdit} className="p-1.5 rounded-lg text-[rgba(14,19,27,0.5)] hover:bg-[#F8F9FC] transition-colors" title="Cancel">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => {
                                if (actionMenuId === t.id) {
                                  setActionMenuId(null);
                                } else {
                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  setActionMenuPos({
                                    top: rect.bottom + 4,
                                    left: Math.max(8, Math.min(rect.right - 180, window.innerWidth - 188)),
                                  });
                                  setActionMenuId(t.id);
                                }
                              }}
                              className="p-1.5 rounded-lg text-[rgba(14,19,27,0.8)] hover:bg-[#F8F9FC] transition-colors"
                            >
                              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                                <circle cx="10" cy="4" r="1.5" />
                                <circle cx="10" cy="10" r="1.5" />
                                <circle cx="10" cy="16" r="1.5" />
                              </svg>
                            </button>
                          )}
                        </td>
                      </motion.tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[#F8F9FC] border-t border-[#E8EBF1]">
                    <td className="px-4 py-3 text-xs font-bold text-[rgba(14,19,27,0.65)] uppercase" colSpan={5}>Total</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#0E131B] text-right tabular-nums">{listTotalMeter.toFixed(2)}</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-sm font-bold text-[#0E131B] text-right tabular-nums">{listTotalWeight.toFixed(2)}</td>
                    <td className="px-4 py-3" colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>}

            {/* Pagination -- Figma style */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 sm:px-4 py-4">
              {/* Centered page buttons */}
              <div className="hidden sm:block flex-1" />
              <div className="flex items-center rounded-xl overflow-hidden border border-[#E1E6EF]">
                <button onClick={() => setPage(1)} disabled={page <= 1} title="First page"
                  className="hidden sm:flex items-center justify-center w-[34px] h-[38px] bg-white border-r border-[#E1E6EF] text-[rgba(14,19,27,0.8)] hover:bg-gray-50 disabled:opacity-30 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} title="Previous page"
                  className="flex items-center justify-center w-[42px] sm:w-[34px] h-[42px] sm:h-[38px] bg-white border-r border-[#E1E6EF] text-[rgba(14,19,27,0.8)] hover:bg-gray-50 disabled:opacity-30 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                {(() => {
                  // Fewer number buttons on phones so the control never overflows the card.
                  const maxBtns = isMobile ? 3 : 5;
                  const half = Math.floor(maxBtns / 2);
                  let start: number;
                  if (totalPages <= maxBtns) start = 1;
                  else if (page <= half + 1) start = 1;
                  else if (page >= totalPages - half) start = totalPages - maxBtns + 1;
                  else start = page - half;
                  return Array.from({ length: Math.min(totalPages, maxBtns) }, (_, i) => {
                    const pageNum = start + i;
                    return (
                      <button key={pageNum} onClick={() => setPage(pageNum)}
                        className={`flex items-center justify-center w-[42px] sm:w-[34px] h-[42px] sm:h-[38px] text-sm font-semibold border-r border-[#E1E6EF] transition-colors ${
                          page === pageNum
                            ? 'bg-[#0055a9] text-white'
                            : 'bg-white text-[rgba(14,19,27,0.8)] hover:bg-gray-50'
                        }`}>
                        {pageNum}
                      </button>
                    );
                  });
                })()}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} title="Next page"
                  className="flex items-center justify-center w-[42px] sm:w-[34px] h-[42px] sm:h-[38px] bg-white sm:border-r border-[#E1E6EF] text-[rgba(14,19,27,0.8)] hover:bg-gray-50 disabled:opacity-30 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
                <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} title="Last page"
                  className="hidden sm:flex items-center justify-center w-[34px] h-[38px] bg-white text-[rgba(14,19,27,0.8)] hover:bg-gray-50 disabled:opacity-30 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                </button>
              </div>

              {/* Show per page */}
              <div className="flex-1 flex items-center justify-center sm:justify-end gap-2">
                <span className="text-sm text-[#0E131B]">Show</span>
                <div className="flex items-center border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] bg-white">
                  <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="px-3 py-2 text-sm text-[#0E131B] bg-transparent focus:outline-none cursor-pointer appearance-none pr-7"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='rgba(14,19,27,0.8)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}>
                    {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="px-6 py-12 sm:p-16 text-center">
            <p className="text-[rgba(14,19,27,0.5)] font-medium mb-2">No entries for {monthLabel}</p>
            <button onClick={openAdd} disabled={!canWrite} title={!canWrite ? 'You have read-only access' : undefined} className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Add Entry</button>
          </div>
        )}
      </div>

      {/* Action dropdown portal */}
      {actionMenuId !== null && createPortal(
        <div
          ref={actionMenuRef}
          className="fixed w-[180px] bg-white rounded-xl border border-[#E1E6EF] shadow-[0_7px_29px_rgba(100,100,111,0.2)] py-1 z-[9999]"
          style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
        >
          {(() => {
            const entry = takaList.find((tk: any) => tk.id === actionMenuId);
            if (!entry) return null;
            return (
              <>
                <button
                  onClick={() => { openEdit(entry); setActionMenuId(null); }}
                  disabled={!canWrite}
                  title={!canWrite ? 'You have read-only access' : undefined}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors gap-3 border-b border-[#E1E6EF]"
                >
                  <svg className="w-5 h-5 text-[#0E131B]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  <span className="flex-1 text-left">Edit</span>
                </button>
                <button
                  onClick={() => {
                    setDeletingTakaId(actionMenuId); setActionMenuId(null);
                    setActionMenuId(null);
                  }}
                  disabled={!canWrite}
                  title={!canWrite ? 'You have read-only access' : undefined}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-[#E02D3C] hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors gap-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  <span className="flex-1 text-left">Delete</span>
                </button>
              </>
            );
          })()}
        </div>,
        document.body
      )}
      {/* Delete Confirmation Modal */}
      {deletingTakaId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-4"
          tabIndex={-1} ref={el => el?.focus()}
          onKeyDown={e => { if (e.key === 'Escape') setDeletingTakaId(null); if (e.key === 'Enter') { e.preventDefault(); deleteMutation.mutate(deletingTakaId); setDeletingTakaId(null); } }}>
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-[#1B202A] mb-2">Delete Taka Entry</h3>
            <p className="text-sm text-gray-500 mb-6">Are you sure you want to delete this taka entry? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingTakaId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { deleteMutation.mutate(deletingTakaId); setDeletingTakaId(null); }}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default TakaInventory;
